import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Scale } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { AnalyticalBalance } from '@/components/lab';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Weighing the Dried Copper',
    content: 'Accurate mass measurement is critical for calculating percent yield. An analytical balance measures to 0.0001 g (0.1 mg) precision. The copper must be completely dry, as residual moisture adds to the apparent mass.',
    whatHappens: 'The evaporating dish containing the dried copper and boiling chips is placed on the analytical balance. The balance displays the total mass.',
    observe: [
      'Wait for balance reading to stabilize',
      'Reading should not fluctuate more than +-0.0002 g',
      'Record the mass to 4 decimal places',
      'Keep balance doors closed during weighing'
    ]
  },
  2: {
    title: 'Recording the Final Mass',
    content: 'The mass of recovered copper is calculated by subtracting the known masses of the empty dish and boiling chips from the total mass:\n\nMass of Cu = Total mass - Dish mass - Chips mass\n\nThis subtraction isolates the mass of copper actually recovered.',
    whatHappens: 'You record the displayed mass and calculate the mass of recovered copper by subtraction. This value will be used to calculate percent yield.',
    observe: [
      'Total mass displayed on balance',
      'Subtract pre-recorded dish mass',
      'Subtract pre-recorded boiling chips mass',
      'Result is mass of recovered copper'
    ]
  }
};

export default function Part9Weighing() {
  const [dishPlaced, setDishPlaced] = useState(false);
  const [weighing, setWeighing] = useState(false);
  const [finalMass, setFinalMass] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  // Simulate a realistic copper recovery (85-98% yield)
  const simulatedInitialMass = 0.5000; // g
  const simulatedYield = 0.85 + Math.random() * 0.13; // 85-98%
  const dishMass = 45.2340; // g (typical porcelain dish)
  const boilingChipsMass = 0.1520; // g
  const theoreticalFinalMass = simulatedInitialMass * simulatedYield;

  const completedSteps = [dishPlaced, finalMass > 0].filter(Boolean).length;

  const handlePlaceDish = useCallback(() => {
    if (!dishPlaced) {
      setDishPlaced(true);
      setWeighing(true);

      // Simulate balance stabilizing
      setTimeout(() => {
        setWeighing(false);
        setFinalMass(dishMass + boilingChipsMass + theoreticalFinalMass);
        setCurrentStep(2);
      }, 2000);
    }
  }, [dishPlaced, theoreticalFinalMass]);

  const copperMass = finalMass > 0 ? finalMass - dishMass - boilingChipsMass : 0;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part X: Final Weighing</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Place on Balance', hint: 'Place dish with copper on pan', done: dishPlaced },
            { id: 2, title: 'Record Mass', hint: 'Record the final mass', done: finalMass > 0 }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Mass data display */}
        {finalMass > 0 && (
          <div className="mass-data">
            <h4><Scale size={14} /> Mass Data</h4>
            <div className="data-row">
              <span>Total (dish + Cu + chips):</span>
              <span className="value">{finalMass.toFixed(4)} g</span>
            </div>
            <div className="data-row">
              <span>Empty dish mass:</span>
              <span className="value">- {dishMass.toFixed(4)} g</span>
            </div>
            <div className="data-row">
              <span>Boiling chips mass:</span>
              <span className="value">- {boilingChipsMass.toFixed(4)} g</span>
            </div>
            <div className="data-row total">
              <span>Recovered copper:</span>
              <span className="value copper">{copperMass.toFixed(4)} g</span>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Final Mass Measurement"
        completedCount={completedSteps}
        totalSteps={2}
        equipment={['Analytical balance', 'Dish with dried copper']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Sample dish */}
            <div
              className={`equipment-station ${!dishPlaced ? 'clickable' : ''}`}
              onClick={handlePlaceDish}
              style={{ cursor: !dishPlaced ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 120 90" width={130} height={100}>
                <defs>
                  <linearGradient id={`dish-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#E8E0D5" />
                    <stop offset="50%" stopColor="#F5F0E8" />
                    <stop offset="100%" stopColor="#E8E0D5" />
                  </linearGradient>
                </defs>

                {/* Porcelain dish */}
                <ellipse cx="60" cy="60" rx="50" ry="15" fill={`url(#dish-${id})`} stroke="#C8C0B5" strokeWidth="2" />
                <path d="M10 60 Q10 40 60 40 Q110 40 110 60" fill="none" stroke="#C8C0B5" strokeWidth="2" />

                {/* Copper */}
                <ellipse cx="60" cy="58" rx="38" ry="10" fill={copperCycleColors.copperPrecipitate} />
                <ellipse cx="55" cy="55" rx="15" ry="5" fill="#D4A373" opacity="0.5" />

                {/* Shiny highlights */}
                <circle cx="45" cy="54" r="2" fill="#FFF" opacity="0.5">
                  <animate attributeName="opacity" values="0.5;0.2;0.5" dur="2s" repeatCount="indefinite" />
                </circle>

                {/* Boiling chips */}
                <circle cx="45" cy="56" r="2" fill={copperCycleColors.boilingChips} />
                <circle cx="75" cy="57" r="2" fill={copperCycleColors.boilingChips} />

                <text x="60" y="85" fontSize="9" fill="#E0E0E0" textAnchor="middle">
                  {dishPlaced ? 'On Balance' : 'Click to Weigh'}
                </text>
              </svg>
            </div>

            {/* Balance */}
            <div className="balance-container">
              <AnalyticalBalance
                mass={weighing ? 0 : finalMass}
                hasSample={dishPlaced}
                stable={!weighing}
                tared={false}
              />
              {weighing && (
                <div className="stabilizing">
                  Stabilizing...
                </div>
              )}
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {!dishPlaced && (
              <div className="status-message info">
                <Scale size={20} />
                Place the dish with dried copper on the balance pan
              </div>
            )}
            {weighing && (
              <div className="status-message weighing">
                <Scale size={20} />
                Balance stabilizing...
              </div>
            )}
            {finalMass > 0 && (
              <div className="status-message success">
                <Check size={20} />
                Mass recorded: {copperMass.toFixed(4)} g copper recovered
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Mass Calculation</h4>
            <div className="equation-box">
              <div className="equation-line">
                <span>Mass of copper = Total mass - Dish mass - Chips mass</span>
              </div>
              {finalMass > 0 && (
                <div className="equation-line result">
                  <span>= {finalMass.toFixed(4)} - {dishMass.toFixed(4)} - {boilingChipsMass.toFixed(4)}</span>
                </div>
              )}
              {finalMass > 0 && (
                <div className="equation-line result final">
                  <span>= {copperMass.toFixed(4)} g Cu</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .balance-container {
            position: relative;
          }

          .stabilizing {
            position: absolute;
            bottom: -30px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            animation: pulse 1s infinite;
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          .mass-data {
            margin-top: 16px;
            padding: 12px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 12px;
          }

          .mass-data h4 {
            display: flex;
            align-items: center;
            gap: 6px;
            margin: 0 0 10px 0;
            color: var(--text-primary);
            font-size: 13px;
          }

          .data-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            color: var(--text-secondary);
          }

          .data-row .value {
            font-family: monospace;
            color: var(--text-primary);
          }

          .data-row.total {
            border-top: 1px solid var(--border);
            margin-top: 8px;
            padding-top: 8px;
            font-weight: 600;
          }

          .data-row.total .value.copper {
            color: ${copperCycleColors.copperPrecipitate};
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.weighing {
            background: rgba(168, 85, 247, 0.2);
            color: #A855F7;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .equation-box {
            background: rgba(0, 0, 0, 0.2);
            padding: 12px;
            border-radius: 6px;
          }

          .equation-line {
            font-family: monospace;
            color: var(--text-secondary);
            padding: 4px 0;
          }

          .equation-line.result {
            color: var(--text-primary);
          }

          .equation-line.final {
            color: ${copperCycleColors.copperPrecipitate};
            font-weight: bold;
            font-size: 16px;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
